/**
 * Which other references an AI enrich call may name under "Notable Variants".
 *
 * The enrichReference prompt lists other watches by reference number, and may
 * only use the ones it is handed in `referenceData.siblingReferences`. This
 * module decides what goes on that list.
 *
 * The list used to be brand-wide: every other parent in the brand, sorted
 * alphabetically, first 50. So a reference was offered watches from unrelated
 * collections, and the model picked them on a shared prefix — a CoPilot
 * chronograph listing ZULU GMTs. The rules here are the ones watchlock's
 * `watchEnrich` already applies (`functions/src/notableVariantCandidates.ts`
 * there); they are ported, not imported, because the two repos share no code.
 *
 * Pure: no Firebase, no Algolia. Both fetch paths — Firestore documents and
 * Algolia hits — feed the same selector, so they cannot disagree.
 */

/**
 * Fewer siblings than this and there is no section to write: the prompt says
 * the block is REQUIRED from this many entries, and omitted below it. The list
 * is withheld entirely below the minimum, so the code and the prompt agree.
 */
export const MIN_SIBLING_REFERENCES = 2;

/** Most siblings ever put in front of the model. */
export const MAX_SIBLING_REFERENCES = 20;

/** The fields of a reference this module reads. */
export interface SiblingRefInput {
  id?: string;
  reference?: unknown;
  model?: unknown;
  nickname?: unknown;
  collection?: unknown;
  parentReferenceId?: unknown;
  isVariant?: unknown;
  /** Stored ref shape: `production.releaseYear` / `startYear`. */
  production?: unknown;
  /** Index-record shape: the same two years, flattened. */
  releaseYear?: unknown;
  startYear?: unknown;
}

/** The reference being enriched. */
export interface SiblingSubject {
  /** Firestore doc id, when the reference has been saved. */
  id?: string;
  reference?: unknown;
  collection?: unknown;
}

/**
 * One entry of `siblingReferences` as the model sees it. `model` and `nickname`
 * are there so each variant's one-line description is read off the catalogue
 * rather than guessed from the reference number; they are left off when empty.
 */
export interface SiblingReference {
  reference: string;
  model?: string;
  nickname?: string;
  /**
   * The sibling's release year (else its production start year), when known.
   * The best dating evidence a metadata run has: a dated sibling close in
   * reference numbering puts the subject in the same era.
   */
  releaseYear?: number;
}

/** A plausible four-digit year, or undefined. */
function asYear(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value.trim()) : value;
  return typeof n === 'number' && Number.isInteger(n) && n >= 1700 && n <= 2200 ? n : undefined;
}

/** A ref's year: release, else production start; stored or index shape. */
export function siblingYear(ref: SiblingRefInput): number | undefined {
  const production = (ref.production && typeof ref.production === 'object' ? ref.production : {}) as Record<string, unknown>;
  return asYear(production.releaseYear) ?? asYear(ref.releaseYear) ?? asYear(production.startYear) ?? asYear(ref.startYear);
}

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Collections are free text; compare them trimmed and case-folded. */
export function normalizeCollection(value: unknown): string {
  return asText(value).toLowerCase();
}

/** Uppercase alphanumerics only, so `CO-CH-B`, `co ch b` and `COCHB` are one reference. */
export function normalizeReferenceKey(value: unknown): string {
  return asText(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * The spellings of a collection worth asking an exact-match store for: the
 * stored value plus its common casings. Whatever comes back is still put
 * through the real case-insensitive comparison in
 * {@link selectCandidateSiblings}. A spelling that differs by more than case or
 * surrounding whitespace is not found.
 */
export function collectionQueryVariants(collection: unknown): string[] {
  const raw = typeof collection === 'string' ? collection : '';
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const titleCase = trimmed
    .toLowerCase()
    .replace(/(^|[\s-])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
  return [...new Set([raw, trimmed, trimmed.toLowerCase(), trimmed.toUpperCase(), titleCase])];
}

/**
 * The subject's catalogue siblings: same collection, not a variant, not the
 * subject itself, one entry per reference number.
 *
 * - SAME COLLECTION, and only that. A subject with no collection has no
 *   siblings — there is deliberately no brand-wide fallback, because a
 *   brand-wide list is exactly what produced cross-collection "variants".
 * - NOT A VARIANT: `isVariant === true` or a `parentReferenceId`. Variants are
 *   not consumer-facing, so listing one sends a reader to a reference they
 *   cannot look up.
 * - NOT THE SUBJECT: by doc id, and by reference key. The key test matters for
 *   a variant doc that shares its parent's number — without it the variant
 *   would list its own parent as a different watch.
 *
 * Ordered so the cap keeps the most useful entries when a collection is large:
 * dated references first, then those carrying a nickname, then by reference
 * number, so the list is the same on every run.
 */
export function selectCandidateSiblings(
  subject: SiblingSubject,
  refs: readonly SiblingRefInput[],
  cap: number = MAX_SIBLING_REFERENCES,
): SiblingReference[] {
  const collection = normalizeCollection(subject.collection);
  if (!collection) return [];
  const subjectKey = normalizeReferenceKey(subject.reference);

  const byKey = new Map<string, SiblingReference>();
  for (const ref of refs) {
    if (normalizeCollection(ref.collection) !== collection) continue;
    if (ref.isVariant === true || asText(ref.parentReferenceId)) continue;
    if (subject.id && ref.id === subject.id) continue;
    const key = normalizeReferenceKey(ref.reference);
    if (!key || key === subjectKey) continue;

    const model = asText(ref.model);
    const nickname = asText(ref.nickname);
    const releaseYear = siblingYear(ref);
    const candidate: SiblingReference = {
      reference: asText(ref.reference),
      ...(model ? { model } : {}),
      ...(nickname ? { nickname } : {}),
      ...(releaseYear !== undefined ? { releaseYear } : {}),
    };
    // Duplicate reference numbers: keep the doc that says more about itself.
    const existing = byKey.get(key);
    const says = (s: SiblingReference) => (s.releaseYear !== undefined ? 2 : 0) + (s.nickname ? 1 : 0);
    if (!existing || says(candidate) > says(existing)) byKey.set(key, candidate);
  }

  return [...byKey.values()]
    .sort((a, b) => {
      // Dated siblings first, so a large collection's cap keeps the dating
      // evidence; then nicknamed ones, as before, for Notable Variants.
      if ((a.releaseYear !== undefined) !== (b.releaseYear !== undefined)) return a.releaseYear !== undefined ? -1 : 1;
      if (!!a.nickname !== !!b.nickname) return a.nickname ? -1 : 1;
      return a.reference.localeCompare(b.reference, 'en', { numeric: true });
    })
    .slice(0, Math.max(0, cap));
}

/**
 * The `siblingReferences` list to send for a subject: its candidate siblings,
 * or nothing at all when there are fewer than {@link MIN_SIBLING_REFERENCES}.
 *
 * Every caller goes through this, so "how many siblings make a section" has one
 * answer. An empty array means "send no list", which the prompt reads as "omit
 * the section".
 */
export function siblingReferencesFor(
  subject: SiblingSubject,
  refs: readonly SiblingRefInput[],
): SiblingReference[] {
  const candidates = selectCandidateSiblings(subject, refs);
  return candidates.length >= MIN_SIBLING_REFERENCES ? candidates : [];
}
